import React, { useState } from 'react';
import { bool, func, string } from 'prop-types';
import classNames from 'classnames';
import { BsFillCheckCircleFill } from 'react-icons/bs';
import { FormattedMessage } from '../../util/reactIntl';
import { renderRichText } from '../../util/richTextRender';
import { CodeOfConductModalContent, Modal } from '../../components';
import ContactButton from './ContactButton';

import css from './SectionAbout.css';

const STEPS = [1, 2, 3];

const CODE_OF_CONDUCT = 'SectionAbout.trustCodeOfConduct';

// "Verified freelancer" is shown only for authors who have the verified badge.
const trustItems = authorVerified =>
  [
    'SectionAbout.trustFreeToStart',
    authorVerified ? 'SectionAbout.trustVerified' : null,
    CODE_OF_CONDUCT,
  ].filter(Boolean);

// Figma 313:1882: the service description, with the "How it works" (313:1889)
// and "Hire with confidence" (313:1924) cards in a 358px sidebar.
const SectionAbout = props => {
  const {
    descriptionText,
    description,
    authorVerified,
    showContactUser,
    onContactUser,
    onManageDisableScrolling,
  } = props;
  const [isCodeOfConductOpen, setCodeOfConductOpen] = useState(false);

  // No Code of Conduct page exists, so its link opens the same modal as the
  // profile card (ProfileUserCard CoCSection). A placeholder, not a rich-text
  // tag: react-intl 3 formats tags with DOMParser, which SSR doesn't have.
  const codeOfConductLink = (
    <button type="button" className={css.inlineLink} onClick={() => setCodeOfConductOpen(true)}>
      <FormattedMessage id="SectionAbout.trustCodeOfConductLink" />
    </button>
  );

  // Rich text from the editor, rendered through the tag allowlist (the stored
  // HTML is author-controlled); plain `description` for very old listings.
  const descriptionContent = descriptionText ? (
    <div className={css.description}>{renderRichText(descriptionText)}</div>
  ) : description ? (
    <div className={classNames(css.description, css.plainDescription)}>{description}</div>
  ) : null;

  return (
    <div className={css.root}>
      <div className={css.main}>{descriptionContent}</div>
      <aside className={css.sidebar}>
        <div className={css.howItWorks}>
          <h2 className={css.cardTitle}>
            <FormattedMessage id="SectionAbout.howItWorksTitle" />
          </h2>
          <ol className={css.steps}>
            {STEPS.map(step => (
              <li className={css.step} key={step}>
                <span className={css.stepNumber} aria-hidden="true">
                  {step}
                </span>
                <span className={css.stepText}>
                  <span className={css.stepTitle}>
                    <FormattedMessage id={`SectionAbout.step${step}Title`} />
                  </span>
                  <span className={css.stepDescription}>
                    <FormattedMessage id={`SectionAbout.step${step}Text`} />
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </div>
        <div className={css.trustCard}>
          <h2 className={css.cardTitle}>
            <FormattedMessage id="SectionAbout.trustTitle" />
          </h2>
          <ul className={css.checks}>
            {trustItems(authorVerified).map(id => (
              <li className={css.checkItem} key={id}>
                <BsFillCheckCircleFill className={css.checkIcon} aria-hidden="true" />
                <span className={css.checkLabel}>
                  <FormattedMessage
                    id={id}
                    values={id === CODE_OF_CONDUCT ? { codeOfConductLink } : undefined}
                  />
                </span>
              </li>
            ))}
          </ul>
          {showContactUser ? (
            <ContactButton className={css.contactButton} onClick={() => onContactUser()}>
              <FormattedMessage id="SectionAbout.contact" />
            </ContactButton>
          ) : null}
        </div>
      </aside>
      <Modal
        id="SectionAbout.codeOfConduct"
        isOpen={isCodeOfConductOpen}
        onClose={() => setCodeOfConductOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <CodeOfConductModalContent />
      </Modal>
    </div>
  );
};

SectionAbout.defaultProps = {
  descriptionText: null,
  description: null,
  authorVerified: false,
  showContactUser: false,
};

SectionAbout.propTypes = {
  descriptionText: string,
  description: string,
  authorVerified: bool,
  showContactUser: bool,
  onContactUser: func.isRequired,
  onManageDisableScrolling: func.isRequired,
};

export default SectionAbout;
